import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import EnrollmentSuccessModal from '../../components/common/EnrollmentSuccessModal';
import PaymentModal from '../../components/common/PaymentModal';
import { useAuth } from '../../context/AuthContext';
import { CourseApi, PaymentApi } from '../../services/api';
import { useNavigate } from 'react-router-dom';
import '../../styles/Courses.css';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://qorenova-backend-production.up.railway.app';

const Courses = () => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedLevel, setSelectedLevel] = useState('all');
  const [sortBy, setSortBy] = useState('popular');
  const [viewMode, setViewMode] = useState('grid');
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [courses, setCourses] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [stats, setStats] = useState({ total: 0, enrolled: 0, completed: 0 });
  
  // Enrollment state
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [enrolledCourse, setEnrolledCourse] = useState(null);
  const [enrollmentCode, setEnrollmentCode] = useState('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentCourse, setPaymentCourse] = useState(null);
  const [actionMsg, setActionMsg] = useState('');
  const [error, setError] = useState('');
  
  // Course details modal state
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  
  const navigate = useNavigate();
  const { token, isAuthenticated, user } = useAuth();

  // Mock courses data for modern design - AI & Engineering Focus
  const mockCourses = [
    {
      id: 1,
      title: "AI Fundamentals for Engineers",
      description: "Master artificial intelligence basics with practical engineering applications and real-world projects",
      instructor: "Instructor TBA",
      level: "Beginner",
      category: "AI & Machine Learning",
      price: "Free",
      originalPrice: "$149",
      duration: "8 weeks",
      rating: 0,
      reviews: 0,
      students: 0,
      thumbnail: "🤖",
      tags: ["AI", "Machine Learning", "Engineering"],
      featured: true,
      progress: 0,
      badges: ["Q1 2026", "Founding Access"],
      lastUpdated: "2024",
      language: "English"
    },
    {
      id: 2,
      title: "Neural Networks & Deep Learning",
      description: "Build and train neural networks from scratch using Python and TensorFlow",
      instructor: "Instructor TBA",
      level: "Intermediate",
      category: "AI & Machine Learning",
      price: "Free",
      originalPrice: "$199",
      duration: "12 weeks",
      rating: 0,
      reviews: 0,
      students: 0,
      thumbnail: "🧠",
      tags: ["Deep Learning", "Neural Networks", "Python"],
      featured: true,
      progress: 0,
      badges: ["Q1 2026", "Hot"],
      lastUpdated: "2024",
      language: "English"
    },
    {
      id: 3,
      title: "Advanced Engineering Systems",
      description: "Master complex engineering principles, system design, and advanced manufacturing processes",
      instructor: "Instructor TBA",
      level: "Advanced",
      category: "Engineering",
      price: "Free",
      originalPrice: "$249",
      duration: "10 weeks",
      rating: 0,
      reviews: 0,
      students: 0,
      thumbnail: "⚙️",
      tags: ["Engineering", "Systems Design", "Manufacturing"],
      featured: true,
      progress: 0,
      badges: ["Q1 2026", "Pro"],
      lastUpdated: "2024",
      language: "English"
    },
    {
      id: 4,
      title: "Computer Vision & Image Processing",
      description: "Learn computer vision techniques and build AI applications for image recognition",
      instructor: "Instructor TBA",
      level: "Intermediate",
      category: "AI & Machine Learning",
      price: "Free",
      originalPrice: "$179",
      duration: "14 weeks",
      rating: 0,
      reviews: 0,
      students: 0,
      thumbnail: "👁️",
      tags: ["Computer Vision", "AI", "OpenCV"],
      featured: false,
      progress: 0,
      badges: ["Q1 2026"],
      lastUpdated: "2024",
      language: "English"
    },
    {
      id: 5,
      title: "Robotics & Automation",
      description: "Design and program intelligent robots using ROS and modern control systems",
      instructor: "Instructor TBA",
      level: "Advanced",
      category: "Engineering",
      price: "Free",
      originalPrice: "$299",
      duration: "16 weeks",
      rating: 0,
      reviews: 0,
      students: 0,
      thumbnail: "🤖",
      tags: ["Robotics", "Automation", "ROS"],
      featured: false,
      progress: 0,
      badges: ["Q1 2026", "Advanced"],
      lastUpdated: "2024",
      language: "English"
    },
    {
      id: 6,
      title: "Natural Language Processing",
      description: "Build AI applications that understand and generate human language",
      instructor: "Instructor TBA",
      level: "Intermediate",
      category: "AI & Machine Learning",
      price: "Free",
      originalPrice: "$189",
      duration: "10 weeks",
      rating: 0,
      reviews: 0,
      students: 0,
      thumbnail: "💬",
      tags: ["NLP", "AI", "Python"],
      featured: false,
      progress: 0,
      badges: ["Q1 2026"],
      lastUpdated: "2024",
      language: "English"
    },
    {
      id: 7,
      title: "AI-Driven CAD & 3D Modeling",
      description: "Master AI-enhanced CAD design with modern 3D modeling tools and automation",
      instructor: "Instructor TBA",
      level: "Beginner",
      category: "Engineering",
      price: "Free",
      originalPrice: "$159",
      duration: "8 weeks",
      rating: 0,
      reviews: 0,
      students: 0,
      thumbnail: "📐",
      tags: ["CAD", "3D Modeling", "AI"],
      featured: false,
      progress: 0,
      badges: ["Q1 2026", "New"],
      lastUpdated: "2024",
      language: "English"
    },
    {
      id: 8,
      title: "Machine Learning for Engineers",
      description: "Apply machine learning algorithms to solve real-world engineering problems",
      instructor: "Instructor TBA",
      level: "Intermediate",
      category: "AI & Machine Learning",
      price: "Free",
      originalPrice: "$219",
      duration: "12 weeks",
      rating: 0,
      reviews: 0,
      students: 0,
      thumbnail: "📊",
      tags: ["ML", "Engineering", "Data Science"],
      featured: false,
      progress: 0,
      badges: ["Q1 2026", "Popular"],
      lastUpdated: "2024",
      language: "English"
    }
  ];

  const categories = [
    { id: 'all', name: t('coursesPage.allCourses'), icon: '🌟', count: mockCourses.length },
    { id: 'AI & Machine Learning', name: t('coursesPage.aiML'), icon: '🤖', count: 5 },
    { id: 'Engineering', name: t('coursesPage.engineering'), icon: '⚙️', count: 3 }
  ];

  const levels = [
    { id: 'all', name: t('coursesPage.allLevels'), icon: '🎯' },
    { id: 'Beginner', name: t('coursesPage.beginner'), icon: '🌱' },
    { id: 'Intermediate', name: t('coursesPage.intermediate'), icon: '🚀' },
    { id: 'Advanced', name: t('coursesPage.advanced'), icon: '⭐' }
  ];

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      // Fetch courses that should display on regular courses page
      // This includes courses with displayPage='courses' or displayPage='both'
      const response = await fetch(`${API_BASE}/api/courses?displayPage=courses&limit=100`);
      const data = await response.json();
      
      if (data.success && data.data.courses) {
        // Transform backend courses to match frontend format
        const transformedCourses = data.data.courses.map(course => ({
          id: course._id,
          title: course.title,
          description: course.description,
          instructor: course.instructor?.name || 'Instructor TBA',
          level: course.level.charAt(0).toUpperCase() + course.level.slice(1),
          category: course.category,
          price: course.pricing?.price === 0 ? 'Free' : `$${course.pricing?.price || 0}`,
          originalPrice: course.pricing?.price > 0 ? `$${(course.pricing.price * 1.5).toFixed(0)}` : null,
          duration: course.totalDuration ? `${Math.ceil(course.totalDuration / 60 / 7)} weeks` : '8 weeks',
          rating: course.averageRating || 0,
          reviews: course.reviewCount || 0,
          students: course.enrollmentCount || 0,
          thumbnail: getCategoryIcon(course.category),
          tags: [course.category, course.level],
          featured: course.featured || false,
          progress: 0,
          badges: getBadges(course),
          lastUpdated: new Date(course.updatedAt || course.createdAt).getFullYear().toString(),
          language: 'English',
          _id: course._id, // Keep original ID for API calls
          status: course.status
        }));
        
        setCourses(transformedCourses);
        setStats({
          total: transformedCourses.length,
          enrolled: 0,
          completed: 0
        });
      } else {
        // Fallback to mock data if API fails
        setCourses(mockCourses);
        setStats({
          total: mockCourses.length,
          enrolled: 0,
          completed: 0
        });
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
      // Fallback to mock data
      setCourses(mockCourses);
      setStats({
        total: mockCourses.length,
        enrolled: 0,
        completed: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'ai-sustainability': '🤖',
      'green-manufacturing': '🏭',
      'renewable-energy': '⚡',
      'climate-tech': '🌍',
      'circular-economy': '♻️',
      'sustainable-design': '🎨',
      'carbon-management': '🌱',
      'smart-cities': '🏙️',
      'environmental-engineering': '🔧',
      'clean-tech': '✨',
      'sustainability-leadership': '👔',
      'data-science': '📊',
      'AI & Machine Learning': '🤖',
      'Engineering': '⚙️'
    };
    return icons[category] || '📚';
  };

  const getBadges = (course) => {
    const badges = [];
    if (course.createdAt) {
      const createdDate = new Date(course.createdAt);
      const now = new Date();
      const daysDiff = (now - createdDate) / (1000 * 60 * 60 * 24);
      if (daysDiff < 30) badges.push('New');
    }
    if (course.enrollmentCount > 100) badges.push('Popular');
    if (course.featured) badges.push('Featured');
    // Don't add a default badge if none apply
    return badges;
  };

  useEffect(() => {
    let filtered = [...courses];

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(course =>
        course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        course.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        course.instructor.toLowerCase().includes(searchTerm.toLowerCase()) ||
        course.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(course => course.category === selectedCategory);
    }

    // Filter by level
    if (selectedLevel !== 'all') {
      filtered = filtered.filter(course => course.level === selectedLevel);
    }

    // Sort courses
    switch (sortBy) {
      case 'popular':
        filtered.sort((a, b) => b.students - a.students);
        break;
      case 'rating':
        filtered.sort((a, b) => b.rating - a.rating);
        break;
      case 'price-low':
        filtered.sort((a, b) => {
          const priceA = a.price === 'Free' ? 0 : parseInt(a.price.replace('$', ''));
          const priceB = b.price === 'Free' ? 0 : parseInt(b.price.replace('$', ''));
          return priceA - priceB;
        });
        break;
      case 'price-high':
        filtered.sort((a, b) => {
          const priceA = a.price === 'Free' ? 0 : parseInt(a.price.replace('$', ''));
          const priceB = b.price === 'Free' ? 0 : parseInt(b.price.replace('$', ''));
          return priceB - priceA;
        });
        break;
      case 'newest':
        filtered.sort((a, b) => new Date(b.lastUpdated) - new Date(a.lastUpdated));
        break;
      default:
        break;
    }

    setFilteredCourses(filtered);
  }, [courses, searchTerm, selectedCategory, selectedLevel, sortBy]);

  const handleEnroll = async (course) => {
    
    try {
      if (!isAuthenticated) {
        setActionMsg(t('coursesPage.loginToEnroll'));
        navigate('/login');
        return;
      }
      setActionMsg('');
      setLoading(true);
      
      // Prefer backend _id; if missing (static card), try to find backend course by title
      let courseId = course._id || course.id;
      
      // Check if course is free first by examining price
      const priceStr = course.price || '$0';
      const priceNum = parseFloat(priceStr.replace(/[$,]/g, ''));
      
      // If course is free, attempt direct enrollment
      if (priceNum === 0 || priceStr.toLowerCase() === 'free') {
        try {
          // For demo purposes, we'll simulate enrollment for mock courses
          if (course.id && !course._id) {
            
            // Store demo enrollment in localStorage
            try {
              // Use user-specific key to prevent cross-user data leakage
              const userId = user?._id || user?.id || 'anonymous';
              const storageKey = `demoEnrollments_${userId}`;
              const demoEnrollments = JSON.parse(localStorage.getItem(storageKey) || '[]');
              
              // Generate a demo enrollment code
              const generateDemoCode = () => {
                const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
                const part1 = Array(3).fill(0).map(() => chars[Math.floor(Math.random() * chars.length)]).join('');
                const part2 = Math.floor(100 + Math.random() * 900);
                const part3 = Array(3).fill(0).map(() => chars[Math.floor(Math.random() * chars.length)]).join('');
                return `${part1}-${part2}-${part3}`;
              };
              
              const enrollmentCode = generateDemoCode();
              
              const enrollmentData = {
                _id: `demo-${course.id}-${Date.now()}`,
                enrollmentCode,
                labAccess: [
                  { labId: 'physics', labName: 'Engineering Physics Playground', accessLevel: 'full' },
                  { labId: 'semicon-mech', labName: 'Semiconductor Mechanical Studio', accessLevel: 'full' }
                ],
                course: {
                  _id: `demo-${course.id}`,
                  title: course.title,
                  description: course.description,
                  category: course.category,
                  level: course.level,
                  thumbnail: course.thumbnail,
                  instructor: {
                    name: course.instructor
                  }
                },
                student: {
                  _id: user?._id || user?.id || 'demo-student',
                  name: user?.name || user?.firstName + ' ' + user?.lastName || 'Student',
                  email: user?.email || 'student@example.com'
                },
                status: 'active',
                enrollmentDate: new Date().toISOString(),
                progress: {
                  percentageComplete: 0,
                  completedLessons: 0,
                  totalLessons: 0
                },
                paymentStatus: 'completed'
              };
              
              // Check if already enrolled in this demo course
              const alreadyEnrolled = demoEnrollments.some(e => 
                e.course?.title === course.title || e.course?._id === `demo-${course.id}`
              );
              
              if (!alreadyEnrolled) {
                demoEnrollments.push(enrollmentData);
                localStorage.setItem(storageKey, JSON.stringify(demoEnrollments));
              } else {
              }
              
              // Set flag for portal to refresh
              localStorage.setItem('portalShouldRefresh', 'true');
            } catch (storageError) {
              console.error('Error storing demo enrollment:', storageError);
            }
            
            setEnrolledCourse(course);
            setEnrollmentCode(enrollmentCode);
            setShowSuccessModal(true);
            setLoading(false);
            return;
          }
          
          // Real API enrollment for backend courses
          const resp = await CourseApi.enroll(token, courseId);
          setEnrolledCourse(course);
          setShowSuccessModal(true);
          setLoading(false);
          return resp;
        } catch (enrollError) {
          // Handle static course fallback
          const isStaticCourse = enrollError.message?.includes('not found') || 
                                enrollError.message?.includes('404') || 
                                enrollError.message?.includes('Invalid course ID') ||
                                enrollError.message?.includes('400') ||
                                enrollError.status === 400 ||
                                enrollError.status === 404;
                                
          if (isStaticCourse) {
            setEnrolledCourse(course);
            setShowSuccessModal(true);
            setLoading(false);
            return;
          }
          throw enrollError;
        }
      } else {
        // Course requires payment
        setPaymentCourse(course);
        setShowPaymentModal(true);
        setLoading(false);
      }
    } catch (error) {
      console.error('Enrollment error:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        response: error.response
      });
      const errorMessage = error.message || t('coursesPage.enrollmentFailed') || 'Enrollment failed. Please try again.';
      setError(errorMessage);
      alert(`Enrollment Error: ${errorMessage}`); // Show alert for visibility
      setLoading(false);
    }
  };

  const handlePaymentSuccess = async (paymentData) => {
    try {
      setLoading(true);
      setShowPaymentModal(false);
      
      // Generate enrollment code
      const generateDemoCode = () => {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
        const part1 = Array(3).fill(0).map(() => chars[Math.floor(Math.random() * chars.length)]).join('');
        const part2 = Math.floor(100 + Math.random() * 900);
        const part3 = Array(3).fill(0).map(() => chars[Math.floor(Math.random() * chars.length)]).join('');
        return `${part1}-${part2}-${part3}`;
      };
      
      const enrollmentCode = generateDemoCode();
      
      // Check if this is a mock/test payment
      const isMockPayment = paymentData?.payment?.isMock || paymentData?.isMock || paymentData?.testMode;
      
      
      // For mock payments - save to database AND localStorage as backup
      if (isMockPayment) {
        
        try {
          // Save to database first
          const token = localStorage.getItem('authToken');
          const enrollmentPayload = {
            courseId: paymentCourse._id || paymentCourse.id,
            paymentStatus: 'completed',
            paymentAmount: parseFloat(paymentCourse.price) || 0,
            enrollmentCode: enrollmentCode,
            labAccess: [
              { labId: 'physics', labName: 'Engineering Physics Playground', accessLevel: 'full' },
                  { labId: 'semicon-mech', labName: 'Semiconductor Mechanical Studio', accessLevel: 'full' },
              { labId: 'medphysics', labName: 'Medical Physics Lab', accessLevel: 'full' }
            ]
          };

          
          const response = await fetch(`${API_BASE}/api/enrollments`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(enrollmentPayload)
          });

          const result = await response.json();
          
          if (response.ok && result.success) {
            
            // Also save to localStorage as backup
            const userId = user?._id || user?.id || 'anonymous';
            const storageKey = `demoEnrollments_${userId}`;
            const demoEnrollments = JSON.parse(localStorage.getItem(storageKey) || '[]');
            
            // Add to localStorage if not already there
            const alreadyInStorage = demoEnrollments.some(e => e.enrollmentCode === enrollmentCode);
            if (!alreadyInStorage) {
              demoEnrollments.push({
                _id: result.data.enrollment._id,
                enrollmentCode: result.data.enrollment.enrollmentCode,
                labAccess: result.data.enrollment.labAccess,
                course: result.data.enrollment.course,
                student: result.data.enrollment.student,
                status: result.data.enrollment.status,
                enrollmentDate: result.data.enrollment.enrollmentDate,
                progress: result.data.enrollment.progress,
                paymentStatus: result.data.enrollment.payment?.status || 'completed',
                paymentAmount: `$${result.data.enrollment.payment?.amount || paymentCourse.price}`
              });
              localStorage.setItem(storageKey, JSON.stringify(demoEnrollments));
            }
            
            // Show success modal
            setEnrolledCourse(paymentCourse);
            setEnrollmentCode(result.data.enrollment.enrollmentCode);
            setShowSuccessModal(true);
            
            // Set refresh flag for portal
            localStorage.setItem('enrollmentRefresh', 'true');
            setLoading(false);
            return;
            
          } else {
            console.warn('⚠️ Database save failed, using localStorage fallback:', result.message);
            throw new Error(result.message || 'Failed to save to database');
          }
          
        } catch (dbError) {
          console.error('❌ Database enrollment failed, falling back to localStorage:', dbError);
          // Fall through to localStorage-only save below
        }
      }
      
      // Fallback: localStorage-only save (for database errors or real Stripe payments)
      if (isMockPayment) {
        
        // Store demo enrollment in localStorage
        try {
          const userId = user?._id || user?.id || 'anonymous';
          const storageKey = `demoEnrollments_${userId}`;
          const demoEnrollments = JSON.parse(localStorage.getItem(storageKey) || '[]');
          
          // Use _id if available (backend course), otherwise use id (demo course)
          const courseIdentifier = paymentCourse._id || paymentCourse.id || 'unknown';
          
          const enrollmentData = {
            _id: `demo-enroll-${courseIdentifier}-${Date.now()}`,
            enrollmentCode,
            labAccess: [
              { labId: 'physics', labName: 'Engineering Physics Playground', accessLevel: 'full' },
                  { labId: 'semicon-mech', labName: 'Semiconductor Mechanical Studio', accessLevel: 'full' },
              { labId: 'medphysics', labName: 'Medical Physics Lab', accessLevel: 'full' }
            ],
            course: {
              _id: paymentCourse._id || `demo-${paymentCourse.id}`,
              title: paymentCourse.title,
              description: paymentCourse.description,
              category: paymentCourse.category,
              level: paymentCourse.level,
              thumbnail: paymentCourse.thumbnail,
              instructor: {
                name: paymentCourse.instructor?.name || paymentCourse.instructor || 'Instructor'
              }
            },
            student: {
              _id: user?._id || user?.id || 'demo-student',
              name: user?.name || user?.firstName + ' ' + user?.lastName || 'Student',
              email: user?.email || 'student@example.com'
            },
            status: 'active',
            enrollmentDate: new Date().toISOString(),
            progress: {
              percentageComplete: 0,
              completedLessons: 0,
              totalLessons: 0
            },
            paymentStatus: 'completed',
            paymentAmount: paymentCourse.price
          };
          
          // Check if already enrolled
          const alreadyEnrolled = demoEnrollments.some(e => 
            e.course?.title === paymentCourse.title || 
            e.course?._id === paymentCourse._id ||
            e.course?._id === `demo-${paymentCourse.id}`
          );
          
          if (!alreadyEnrolled) {
            demoEnrollments.push(enrollmentData);
            localStorage.setItem(storageKey, JSON.stringify(demoEnrollments));
          } else {
          }
          
          // Set flag for portal to refresh
          localStorage.setItem('portalShouldRefresh', 'true');
        } catch (storageError) {
          console.error('Error storing demo enrollment:', storageError);
        }
        
        setEnrolledCourse(paymentCourse);
        setEnrollmentCode(enrollmentCode);
        setShowSuccessModal(true);
        setPaymentCourse(null);
        setLoading(false);
        return;
      }
      
      // For real backend courses - payment should already be processed via Stripe
      // The paymentData from PaymentModal contains the enrollment info
      
      if (paymentData.enrollment) {
        // Backend already created enrollment during payment confirmation
        setEnrolledCourse(paymentCourse);
        setEnrollmentCode(paymentData.enrollment.enrollmentCode || enrollmentCode);
        setShowSuccessModal(true);
        setPaymentCourse(null);
        setLoading(false);
        return;
      }
      
      // Fallback: Try to enroll (shouldn't normally reach here with proper payment flow)
      try {
        const courseId = paymentCourse._id;
        const enrollResp = await CourseApi.enroll(token, courseId);
        
        setEnrolledCourse(paymentCourse);
        setEnrollmentCode(enrollResp.data?.enrollmentCode || enrollmentCode);
        setShowSuccessModal(true);
        setPaymentCourse(null);
        setLoading(false);
      } catch (enrollError) {
        console.error('Enrollment after payment failed:', enrollError);
        // Still show success since payment went through
        setEnrolledCourse(paymentCourse);
        setEnrollmentCode(enrollmentCode);
        setShowSuccessModal(true);
        setPaymentCourse(null);
        setLoading(false);
      }
      
    } catch (error) {
      console.error('Payment enrollment error:', error);
      const errorMessage = error.message || t('coursesPage.paymentFailed') || 'Payment failed. Please try again.';
      setError(errorMessage);
      alert(`Payment Error: ${errorMessage}`);
      setLoading(false);
    }
  };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    setEnrolledCourse(null);
    setActionMsg('');
  };

  const handleGoToPortal = () => {
    // Ensure portal will refresh when we navigate to it
    localStorage.setItem('portalShouldRefresh', 'true');
    navigate('/studentportal');
  };

  const handleClosePaymentModal = () => {
    setShowPaymentModal(false);
    setPaymentCourse(null);
  };

  const handleCourseClick = (course) => {
    setSelectedCourse(course);
    setShowDetailsModal(true);
  };

  const handleCloseDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedCourse(null);
  };

  const CourseCard = ({ course }) => (
    <div
      className={`course-card${course.featured ? ' featured' : ''}`}
      onClick={() => handleCourseClick(course)}
    >
      <div className="course-body">
        {course.badges && course.badges.length > 0 && (
          <div className="course-badges">
            {course.badges.slice(0, 2).map((badge, idx) => (
              <span key={idx} className="badge">{badge}</span>
            ))}
          </div>
        )}
        <div className="course-category">{course.category}</div>
        <h3 className="course-title">{course.title}</h3>
        <p className="course-description">{course.description}</p>
        <div className="course-meta">
          <span className="meta-item">
            {course.instructor === "Instructor TBA" ? t('courses.instructorTBA') : course.instructor}
          </span>
          <span className="meta-item">{course.duration}</span>
          <span className="meta-item">
            {course.rating > 0 ? `⭐ ${course.rating}` : t('courses.comingSoon')}
          </span>
        </div>
        <div className="course-footer">
          <div className="course-pricing">
            <span className={`course-price${course.price !== 'Free' && course.price !== t('courses.free') ? ' paid' : ''}`}>
              {course.price}
            </span>
            {course.originalPrice && course.price !== course.originalPrice && (
              <span className="original-price">{course.originalPrice}</span>
            )}
          </div>
          <button
            className={`enroll-btn${loading ? ' loading' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              handleEnroll(course);
            }}
            disabled={loading}
          >
            {loading ? '...' : (course.price === 'Free' || course.price === t('courses.free') ? t('courses.enrollFree') : t('courses.enrollNow'))}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="courses-page">
      <Header />

      {/* Hero Section */}
      <section className="courses-hero">
        <div className="hero-content">
          <h1 className="hero-title">
            Master <span className="highlight">AI & Engineering</span>
          </h1>
          <p className="hero-subtitle">
            Transform your career with cutting-edge courses from industry experts
          </p>
        </div>
      </section>

      {/* Filter Bar */}
      <section className="courses-filters">
        <div className="container">
          <div className="filter-bar">
            <span className="results-count">
              {filteredCourses.length} {filteredCourses.length === 1 ? 'course' : 'courses'}
            </span>
            <div className="filter-controls">
              <input
                type="text"
                placeholder="Search courses..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              <select
                className="filter-select"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <option value="all">All Categories</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
              <select
                className="filter-select"
                value={selectedLevel}
                onChange={(e) => setSelectedLevel(e.target.value)}
              >
                <option value="all">All Levels</option>
                {levels.map(level => (
                  <option key={level.id} value={level.id}>{level.name}</option>
                ))}
              </select>
              <select
                className="filter-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="popular">Most Popular</option>
                <option value="rating">Highest Rated</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="newest">Newest First</option>
              </select>
              <div className="view-toggle">
                <button
                  className={`view-btn${viewMode === 'grid' ? ' active' : ''}`}
                  onClick={() => setViewMode('grid')}
                  aria-label="Grid view"
                >
                  ⊞
                </button>
                <button
                  className={`view-btn${viewMode === 'list' ? ' active' : ''}`}
                  onClick={() => setViewMode('list')}
                  aria-label="List view"
                >
                  ☰
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Courses Grid */}
      <section className="courses-container">
        <div className="container">
          {loading && (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p className="loading-text">{t('coursesPage.loading') || 'Loading courses...'}</p>
            </div>
          )}

          {!loading && filteredCourses.length > 0 && (
            <div className={`courses-grid${viewMode === 'list' ? ' list-view' : ''}`}>
              {filteredCourses.map((course, index) => (
                <CourseCard key={course.id || course._id} course={course} />
              ))}
            </div>
          )}

          {!loading && filteredCourses.length === 0 && (
            <div className="empty-state">
              <h3 className="empty-title">{t('coursesPage.noCourses') || 'No Courses Found'}</h3>
              <p className="empty-text">
                {t('coursesPage.noCoursesDesc') || "Try adjusting your filters or search terms."}
              </p>
              <button
                className="enroll-btn"
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('all');
                  setSelectedLevel('all');
                  setSortBy('popular');
                }}
              >
                {t('coursesPage.resetFilters') || 'Reset Filters'}
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Error Banner */}
      {error && (
        <div className="error-banner">
          <div className="container">
            <div className="banner-content">
              <span>⚠️ {error}</span>
              <button className="banner-close" onClick={() => setError('')}>✕</button>
            </div>
          </div>
        </div>
      )}

      {/* Action Message */}
      {actionMsg && (
        <div className="action-banner">
          <div className="container">
            <div className="banner-content">
              <span>ℹ️ {actionMsg}</span>
              <button className="banner-close" onClick={() => setActionMsg('')}>✕</button>
            </div>
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      {loading && (
        <div className="loading-overlay">
          <div className="loading-overlay-content">
            <div className="spinner"></div>
            <p>{t('coursesPage.processingEnrollment')}</p>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && enrolledCourse && (
        <EnrollmentSuccessModal
          isOpen={showSuccessModal}
          course={enrolledCourse}
          enrollmentCode={enrollmentCode}
          onClose={handleCloseSuccessModal}
          onGoToPortal={handleGoToPortal}
        />
      )}

      {/* Payment Modal */}
      {showPaymentModal && paymentCourse && (
        <PaymentModal
          isOpen={showPaymentModal}
          course={paymentCourse}
          onSuccess={handlePaymentSuccess}
          onClose={handleClosePaymentModal}
        />
      )}

      {/* Course Details Modal */}
      {showDetailsModal && selectedCourse && (
        <div className="course-details-modal-overlay" onClick={handleCloseDetailsModal}>
          <div className="course-details-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={handleCloseDetailsModal}>✕</button>

            <div className="modal-header">
              <div className="modal-category">
                <span className="category-icon">{getCategoryIcon(selectedCourse.category)}</span>
                <span className="category-text">{selectedCourse.category}</span>
              </div>
              <h2 className="modal-title">{selectedCourse.title}</h2>
              <p className="modal-subtitle">{selectedCourse.description}</p>

              <div className="modal-meta-grid">
                <div className="meta-card">
                  <span className="meta-card-icon">👨‍🏫</span>
                  <div className="meta-card-content">
                    <div className="meta-card-label">Instructor</div>
                    <div className="meta-card-value">
                      {selectedCourse.instructor === "Instructor TBA" ? t('courses.instructorTBA') : selectedCourse.instructor}
                    </div>
                  </div>
                </div>
                <div className="meta-card">
                  <span className="meta-card-icon">📊</span>
                  <div className="meta-card-content">
                    <div className="meta-card-label">Level</div>
                    <div className="meta-card-value">{selectedCourse.level}</div>
                  </div>
                </div>
                <div className="meta-card">
                  <span className="meta-card-icon">⏱️</span>
                  <div className="meta-card-content">
                    <div className="meta-card-label">Duration</div>
                    <div className="meta-card-value">{selectedCourse.duration}</div>
                  </div>
                </div>
                <div className="meta-card">
                  <span className="meta-card-icon">👥</span>
                  <div className="meta-card-content">
                    <div className="meta-card-label">Students</div>
                    <div className="meta-card-value">
                      {selectedCourse.students > 0 ? selectedCourse.students.toLocaleString() : 'Be the first!'}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-body">
              <div className="modal-section">
                <h3 className="section-title">What You'll Learn</h3>
                <ul className="learning-points">
                  <li>Master the core concepts and fundamentals of {selectedCourse.category}</li>
                  <li>Build real-world projects that you can add to your portfolio</li>
                  <li>Learn industry best practices and cutting-edge techniques</li>
                  <li>Gain hands-on experience with practical exercises and assignments</li>
                  <li>Develop problem-solving skills applicable to real scenarios</li>
                  <li>Get lifetime access to course materials and updates</li>
                </ul>
              </div>

              <div className="modal-section">
                <h3 className="section-title">Course Description</h3>
                <p className="course-full-description">
                  This comprehensive course is designed to take you from beginner to advanced level in {selectedCourse.category}.
                  You'll start with the fundamentals and gradually progress to more complex topics, ensuring a solid understanding
                  at every step. Our expert instructors bring years of industry experience and academic excellence to deliver
                  content that is both practical and theoretically sound.
                </p>
                <p className="course-full-description">
                  The course includes hands-on projects, quizzes, and assignments to reinforce your learning. You'll have
                  access to our learning platform 24/7, allowing you to learn at your own pace. Join thousands of students
                  who have already transformed their careers with QoreNova Academy.
                </p>
              </div>

              <div className="modal-section">
                <h3 className="section-title">Why Choose This Course</h3>
                <div className="features-grid">
                  <div className="feature-item">
                    <span className="feature-icon">🎓</span>
                    <div className="feature-content">
                      <h4>Expert Instruction</h4>
                      <p>Learn from industry professionals with years of experience</p>
                    </div>
                  </div>
                  <div className="feature-item">
                    <span className="feature-icon">💼</span>
                    <div className="feature-content">
                      <h4>Career Ready</h4>
                      <p>Build skills that employers are actively seeking</p>
                    </div>
                  </div>
                  <div className="feature-item">
                    <span className="feature-icon">🔄</span>
                    <div className="feature-content">
                      <h4>Lifetime Access</h4>
                      <p>Learn at your own pace with unlimited course access</p>
                    </div>
                  </div>
                  <div className="feature-item">
                    <span className="feature-icon">🏆</span>
                    <div className="feature-content">
                      <h4>Certificate</h4>
                      <p>Earn a certificate upon successful completion</p>
                    </div>
                  </div>
                </div>
              </div>

              {selectedCourse.tags && selectedCourse.tags.length > 0 && (
                <div className="modal-section">
                  <h3 className="section-title">Tags</h3>
                  <div className="tags-list">
                    {selectedCourse.tags.map((tag, idx) => (
                      <span key={idx} className="tag-pill">{tag}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <div className="modal-pricing-section">
                <div className="pricing-info">
                  <div className="pricing-label">Course Price</div>
                  <div className="pricing-values">
                    <span className={`modal-price${selectedCourse.price !== 'Free' && selectedCourse.price !== t('courses.free') ? ' paid' : ''}`}>
                      {selectedCourse.price}
                    </span>
                    {selectedCourse.originalPrice && selectedCourse.price !== selectedCourse.originalPrice && (
                      <span className="modal-original-price">{selectedCourse.originalPrice}</span>
                    )}
                  </div>
                  {selectedCourse.price === 'Free' || selectedCourse.price === t('courses.free') ? (
                    <div className="free-badge">Limited Time Free Access</div>
                  ) : (
                    <div className="discount-badge">Special Launch Discount</div>
                  )}
                </div>
              </div>
              <button
                className="modal-enroll-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCloseDetailsModal();
                  handleEnroll(selectedCourse);
                }}
                disabled={loading}
              >
                {loading ? 'Processing...' : (selectedCourse.price === 'Free' || selectedCourse.price === t('courses.free') ? 'Enroll for Free' : 'Enroll Now')}
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Courses;
